<template>
    <div class="animated fadeIn">
        <div class="kgr-row">
            <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--top kgr-row">
                <div class="kgr-postfix__fluid">
                    <a
                        class="kgr-button kgr-button--link"
                        @click="redirectToOverview"
                    >
                        <kgr-icon
                            icon-class="arrow-left"
                            class="kgr-button__icon kgr-button__icon--left kgr-icon"
                        />

                        <span v-t="'backToOverview'"></span>
                    </a>
                </div>
                <div class="kgr-postfix__fixed">
                    <button
                        v-if="translateTo"
                        class="kgr-button kgr-button--accent"
                        @click="showOriginal = !showOriginal"
                    >
                        {{ showOriginal ? $t('Übersetzung anzeigen') : $t('Originaltext anzeigen') }}
                    </button>
                </div>
                <div
                    v-if="!loading && news.canEdit"
                    class="kgr-postfix__fixed"
                >
                    <a
                        class="kgr-button kgr-button--accent"
                        :href="editUrl"
                    >
                        <kgr-icon
                            icon-class="edit"
                            small
                            class="kgr-button__icon kgr-button__icon--left"
                        />
                        <span>{{ $t('News.modifyEntry') }}</span>
                    </a>
                </div>
                <div
                    v-if="canCreate"
                    class="kgr-postfix__fixed"
                >
                    <a
                        class="kgr-button kgr-button--accent"
                        :href="createUrl"
                    >
                        <kgr-icon
                            icon-class="add"
                            small
                            circle
                            highlight
                            class="kgr-button__icon kgr-button__icon--left"
                        />
                        <span>{{ $t('News.addNew') }}</span>
                    </a>
                </div>
            </div>
        </div>
        <hr class="kgr-separator" />
        <div class="kgr-row">
            <kgr-loading v-if="loading" />
            <div
                v-if="!loading"
                class="kgr-float kgr-float--clear"
            >
                <div
                    v-if="news.image"
                    class="kgr-float kgr-float--left kgr-float--inText"
                >
                    <img
                        :src="`/backend/thumbnail/clubNews/${news.id}/news_detail_thumb/${news.image}`"
                        alt="News Image"
                    />
                </div>
                <h3 class="kgr-headline kgr-headline--h3">
                    {{ !showOriginal && translateTo && news.translations?.title ? news.translations.title : news.title }}
                    <kgr-clubity-publication-icon
                        :clearing="news.clearing"
                    />
                </h3>
                <div class="app-meta kgr-row">
                    {{ metaText }}
                </div>
                <div
                    v-if="news.federation"
                    class="kgr-row"
                >
                    <span class="kgr-badge kgr-badge--info">
                        {{ $t('Trägernachricht') }}
                    </span>
                </div>
                <div
                    class="kgr-wysiwyg"
                    v-html="!showOriginal && translateTo && news.translations?.text ? news.translations.text : news.text "
                >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { format, parseISO } from '@legacy/js/date/date-fns.js';
import ClubityPublicationIcon from '../../../components/common/icon/ClubityPublicationIcon.vue';
import Icon from '../../../components/common/icon/Icon.vue';
import Loading from '../../../components/common/Loading.vue';
import axios from 'axios';
import { getLanguageFromCookie } from '../../../js/service/LanguageHelper.js';

export default {
    components: {
        'kgr-icon': Icon,
        'kgr-loading': Loading,
        'kgr-clubity-publication-icon': ClubityPublicationIcon,
    },
    props: {
        newsId: {
            type: Number,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        },
        overviewUrl: {
            type: String,
            required: true,
        },
        canCreate: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            error: new Error(),
            loading: true,
            showOriginal: false,
            translateTo: null,
            news: null,
        };
    },
    computed: {
        editUrl() {
            return `/backend/clubs/news/${this.newsId}/edit`;
        },
        createUrl() {
            return `/backend/clubs/${this.clubId}/news/create`;
        },
        metaText() {
            const parts = [this.getParsedDate(this.news.publishedAt)];

            if (this.news.clearing === 'all_employees_and_all_members') {
                parts.push(this.$t('Für alle freigegeben'));
            } else if (this.news.clearing !== 'all_employees') {
                const details = [];
                if (this.news.departments?.length) {
                    details.push(`${this.$t('Abteilungen')}: ${this.news.departments.join(', ')}`);
                }
                if (this.news.teams?.length) {
                    details.push(`${this.$t('Teams')}: ${this.news.teams.join(', ')}`);
                }
                if (details.length) {
                    parts.push(details.join(' '));
                }
            }

            return parts.join(', ');
        },
        isMissingTranslation() {
            const news = this.news;
            const hasTitle = news.title !== null && news.title.trim() !== '';
            const hasTeaser = news.teaser !== null && news.teaser.trim() !== '';
            const hasText = news.text !== null && news.text.trim() !== '';

            const missingTitle = hasTitle && !news.translations?.title;
            const missingTeaser = hasTeaser && !news.translations?.teaser;
            const missingText = hasText && !news.translations?.text;

            return missingTitle || missingTeaser || missingText;
        },
    },
    created() {
        const savedLang = getLanguageFromCookie();
        if (savedLang && savedLang !== 'de_DE') {
            this.translateTo = savedLang;
            this.translate();
        }
        this.load();
    },
    methods: {
        redirectToOverview() {
            window.location = this.overviewUrl;
        },
        getParsedDate(date) {
            const parsedDate = parseISO(date);
            const formatted = format(parsedDate, 'dd.MM.yyyy HH:mm');

            return formatted;
        },
        async translate() {
            await this.load();
            if (!this.isMissingTranslation) {
                return;
            }
            this.loading = true;
            await axios
                .post(`/club/clubs/${this.clubId}/news/translations`, { ids: [this.newsId], translationLanguage: this.translateTo })
                .catch(() => this.$notify.error('Beim Übersetzen ist ein Fehler aufgetreten.'));
            this.load();
        },
        async load() {
            this.loading = true;
            try {
                const response = await axios.get(`/club/news/${this.newsId}`);
                this.news = response.data.data;
            } catch (e) {
                this.$notify.error('error.loadingData');
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
</style>
